import { useEffect, useRef, useCallback } from 'react';

interface UseExamRestrictionsProps {
    onComplete: () => void;
    onTabChange?: () => void;
    initialViolationCount?: number;
}

const useExamRestrictions = ({ onComplete, onTabChange, initialViolationCount = 0 }: UseExamRestrictionsProps) => {
    const fullscreenDialogRef = useRef<HTMLDivElement | null>(null);
    const tabChangeDialogRef = useRef<HTMLDivElement | null>(null);
    const isInitializedRef = useRef(false);
    const isFullscreenDialogOpenRef = useRef(false);
    const isTabChangeDialogOpenRef = useRef(false);
    const tabChangeCountRef = useRef(initialViolationCount);

    // Add navigation function
    const navigateToThankYou = useCallback(() => {
        window.location.href = '/exam/thankyou';
    }, []);

    // Block browser refresh
    const blockRefresh = useCallback((e: BeforeUnloadEvent) => {
        e.preventDefault();
        return false;
    }, []);

    // Prevent default actions
    const preventDefaultAction = (e: Event) => {
        e.preventDefault();
        return false;
    };

    // Prevent keyboard shortcuts
    const preventKeyboardShortcuts = (e: KeyboardEvent) => {
        if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'v' || e.key === 'a')) {
            e.preventDefault();
            return false;
        }
    };

    // Prevent context menu
    const preventContextMenu = (e: MouseEvent) => {
        e.preventDefault();
        return false;
    };

    // Request full screen
    const requestFullScreen = async () => {
        try {
            const element = document.documentElement;
            if (element.requestFullscreen) {
                await element.requestFullscreen();
            } else if ((element as any).webkitRequestFullscreen) {
                await (element as any).webkitRequestFullscreen();
            } else if ((element as any).msRequestFullscreen) {
                await (element as any).msRequestFullscreen();
            }
        } catch (error) {
            console.warn('Failed to enter full screen:', error);
        }
    };

    // Show fullscreen warning dialog
    const showFullscreenWarningDialog = useCallback(() => {
        if (isFullscreenDialogOpenRef.current || fullscreenDialogRef.current) {
            return;
        }

        isFullscreenDialogOpenRef.current = true;

        // Add overlay
        const overlay = document.createElement('div');
        overlay.style.position = 'fixed';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.width = '100%';
        overlay.style.height = '100%';
        overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
        overlay.style.zIndex = '99998';
        document.body.appendChild(overlay);

        const dialog = document.createElement('div');
        dialog.style.position = 'fixed';
        dialog.style.top = '50%';
        dialog.style.left = '50%';
        dialog.style.transform = 'translate(-50%, -50%)';
        dialog.style.backgroundColor = 'white';
        dialog.style.padding = '24px';
        dialog.style.borderRadius = '12px';
        dialog.style.color = '#1C2025';
        dialog.style.textAlign = 'center';
        dialog.style.zIndex = '99999';
        dialog.style.minWidth = '400px';
        dialog.style.boxShadow = '0px 4px 20px rgba(0, 0, 0, 0.15)';
        dialog.style.border = '2px solid #ea7a23';
        dialog.style.animation = 'fadeIn 0.3s ease-in-out';

        const iconContainer = document.createElement('div');
        iconContainer.style.marginBottom = '16px';
        iconContainer.innerHTML = `<svg width="48" height="48" viewBox="0 0 24 24" fill="none">
            <path d="M12 5.99L19.53 19H4.47L12 5.99ZM12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v4h2v-4z" fill="#ea7a23"/>
        </svg>`;
        dialog.appendChild(iconContainer);

        const messageElement = document.createElement('p');
        messageElement.textContent = 'Please return to full-screen mode to continue the exam.';
        messageElement.style.marginBottom = '24px';
        messageElement.style.fontSize = '16px';
        messageElement.style.lineHeight = '1.5';
        dialog.appendChild(messageElement);

        const button = document.createElement('button');
        button.textContent = 'Enter Full Screen';
        button.style.padding = '10px 24px';
        button.style.backgroundColor = '#ea7a23';
        button.style.color = 'white';
        button.style.border = 'none';
        button.style.borderRadius = '8px';
        button.style.cursor = 'pointer';
        button.style.fontSize = '14px';
        button.style.fontWeight = '600';
        button.style.transition = 'all 0.2s ease';
        button.onmouseover = () => {
            button.style.backgroundColor = '#d86a1f';
        };
        button.onmouseout = () => {
            button.style.backgroundColor = '#ea7a23';
        };
        button.onclick = () => {
            if (dialog && document.body.contains(dialog)) {
                dialog.style.animation = 'fadeOut 0.3s ease-in-out';
                overlay.style.animation = 'fadeOut 0.3s ease-in-out';
                setTimeout(() => {
                    if (dialog && document.body.contains(dialog)) {
                        document.body.removeChild(dialog);
                        document.body.removeChild(overlay);
                        fullscreenDialogRef.current = null;
                        isFullscreenDialogOpenRef.current = false;
                    }
                    requestFullScreen();
                }, 300);
            }
        };
        dialog.appendChild(button);

        fullscreenDialogRef.current = dialog;
        document.body.appendChild(dialog);
    }, []);

    // Close fullscreen warning dialog
    const closeFullscreenDialog = useCallback(() => {
        if (fullscreenDialogRef.current && document.body.contains(fullscreenDialogRef.current)) {
            fullscreenDialogRef.current.style.animation = 'fadeOut 0.3s ease-in-out';
            const overlay = document.querySelector('div[style*="z-index: 99998"]') as HTMLDivElement;
            if (overlay) {
                overlay.style.animation = 'fadeOut 0.3s ease-in-out';
                setTimeout(() => {
                    if (overlay && document.body.contains(overlay)) {
                        document.body.removeChild(overlay);
                    }
                }, 300);
            }
            setTimeout(() => {
                if (fullscreenDialogRef.current && document.body.contains(fullscreenDialogRef.current)) {
                    document.body.removeChild(fullscreenDialogRef.current);
                    fullscreenDialogRef.current = null;
                    isFullscreenDialogOpenRef.current = false;
                }
            }, 300);
        }
    }, []);

    // Show tab change warning dialog
    const showTabChangeWarningDialog = useCallback(() => {
        // Return if dialog is already showing
        if (isTabChangeDialogOpenRef.current || tabChangeDialogRef.current) {
            console.log('Preventing duplicate tab change dialog:', {
                isOpen: isTabChangeDialogOpenRef.current,
                hasRef: !!tabChangeDialogRef.current
            });
            return;
        }

        console.log('Creating new tab change dialog');
        isTabChangeDialogOpenRef.current = true;

        // Add overlay
        const overlay = document.createElement('div');
        overlay.style.position = 'fixed';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.width = '100%';
        overlay.style.height = '100%';
        overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
        overlay.style.zIndex = '99997';
        document.body.appendChild(overlay);

        const isLastWarning = tabChangeCountRef.current >= 3;

        const dialog = document.createElement('div');
        dialog.style.position = 'fixed';
        dialog.style.top = '50%';
        dialog.style.left = '50%';
        dialog.style.transform = 'translate(-50%, -50%)';
        dialog.style.backgroundColor = 'white';
        dialog.style.padding = '24px';
        dialog.style.borderRadius = '12px';
        dialog.style.color = '#1C2025';
        dialog.style.textAlign = 'center';
        dialog.style.zIndex = '99998';
        dialog.style.minWidth = '400px';
        dialog.style.boxShadow = '0px 4px 20px rgba(0, 0, 0, 0.15)';
        dialog.style.border = '2px solid #ff4444';
        dialog.style.animation = 'fadeIn 0.3s ease-in-out';

        const iconContainer = document.createElement('div');
        iconContainer.style.marginBottom = '16px';
        iconContainer.innerHTML = `<svg width="48" height="48" viewBox="0 0 24 24" fill="none">
            <path d="M12 5.99L19.53 19H4.47L12 5.99ZM12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v4h2v-4z" fill="#ff4444"/>
        </svg>`;
        dialog.appendChild(iconContainer);

        // Add attempt counter
        const attemptCounter = document.createElement('div');
        attemptCounter.style.marginBottom = '16px';
        attemptCounter.style.color = '#ff4444';
        attemptCounter.style.fontSize = '14px';
        attemptCounter.style.fontWeight = '600';
        attemptCounter.textContent = isLastWarning ? 'Final Warning' : `Warning ${tabChangeCountRef.current}/3`;
        dialog.appendChild(attemptCounter);

        const messageElement = document.createElement('p');
        messageElement.textContent = isLastWarning
            ? 'You have exceeded the maximum number of tab switches. Your exam will be submitted in 3 seconds.'
            : `Warning: Tab switching detected! ${3 - tabChangeCountRef.current} attempts remaining before auto-submission.`;
        messageElement.style.marginBottom = '24px';
        messageElement.style.fontSize = '16px';
        messageElement.style.lineHeight = '1.5';
        dialog.appendChild(messageElement);

        if (!isLastWarning) {
            const button = document.createElement('button');
            button.textContent = 'Return to Exam';
            button.style.padding = '10px 24px';
            button.style.backgroundColor = '#ff4444';
            button.style.color = 'white';
            button.style.border = 'none';
            button.style.borderRadius = '8px';
            button.style.cursor = 'pointer';
            button.style.fontSize = '14px';
            button.style.fontWeight = '600';
            button.style.transition = 'all 0.2s ease';
            button.onmouseover = () => {
                button.style.backgroundColor = '#ff3333';
            };
            button.onmouseout = () => {
                button.style.backgroundColor = '#ff4444';
            };
            button.onclick = () => {
                if (dialog && document.body.contains(dialog)) {
                    dialog.style.animation = 'fadeOut 0.3s ease-in-out';
                    overlay.style.animation = 'fadeOut 0.3s ease-in-out';
                    setTimeout(() => {
                        if (dialog && document.body.contains(dialog)) {
                            document.body.removeChild(dialog);
                            document.body.removeChild(overlay);
                            tabChangeDialogRef.current = null;
                            isTabChangeDialogOpenRef.current = false;
                        }
                        requestFullScreen();
                    }, 300);
                }
            };
            dialog.appendChild(button);
        } else {
            // Add countdown for last warning
            const countdownElement = document.createElement('div');
            countdownElement.style.marginTop = '16px';
            countdownElement.style.color = '#ff4444';
            countdownElement.style.fontSize = '20px';
            countdownElement.style.fontWeight = '600';
            dialog.appendChild(countdownElement);

            let countdown = 3;
            const updateCountdown = () => {
                countdownElement.textContent = `Submitting in ${countdown} seconds...`;
                if (countdown > 0) {
                    countdown--;
                    setTimeout(updateCountdown, 1000);
                }
            };
            updateCountdown();

            // Submit exam after countdown
            setTimeout(() => {
                if (dialog && document.body.contains(dialog)) {
                    document.body.removeChild(dialog);
                    document.body.removeChild(overlay);
                }
                onComplete();
                setTimeout(() => {
                    navigateToThankYou();
                }, 1000);
            }, 3000);
        }

        tabChangeDialogRef.current = dialog;
        document.body.appendChild(dialog);
    }, [onComplete, navigateToThankYou]);

    // Block back button
    const blockBackButton = useCallback((e: PopStateEvent) => {
        e.preventDefault();
        return false;
        // window.history.pushState(null, '', window.location.pathname);
        // // Increment tab change count and show warning
        // if (!tabChangeDialogRef.current) {
        //     tabChangeCountRef.current += 1;
        //     console.log('Back button pressed - Incrementing tab change count to:', tabChangeCountRef.current);

        //     // Close fullscreen dialog if it exists
        //     if (isFullscreenDialogOpenRef.current) {
        //         closeFullscreenDialog();
        //     }

        //     // Show tab change warning
        //     showTabChangeWarningDialog();
        // }
    }, []);

    // Handle fullscreen change
    const handleFullscreenChange = useCallback(() => {
        if (!document.fullscreenElement) {
            showFullscreenWarningDialog();
        }
    }, [showFullscreenWarningDialog]);

    // Handle visibility change
    const handleVisibilityChange = useCallback(() => {
        if (document.visibilityState === 'hidden') {
            console.log('Tab visibility changed to hidden. Current states:', {
                isOpen: isTabChangeDialogOpenRef.current,
                hasRef: !!tabChangeDialogRef.current
            });

            if (!tabChangeDialogRef.current) {
                tabChangeCountRef.current += 1;
                console.log('Incrementing tab change count to:', tabChangeCountRef.current);

                // Call onTabChange callback if provided
                onTabChange?.();

                // Close fullscreen dialog if it exists
                if (isFullscreenDialogOpenRef.current) {
                    closeFullscreenDialog();
                }

                // Show tab change warning
                showTabChangeWarningDialog();
            }
        }
    }, [showTabChangeWarningDialog, closeFullscreenDialog, onTabChange]);

    // Reset function
    const resetWarningCount = useCallback(() => {
        console.log('Resetting all warnings and dialogs');
        isFullscreenDialogOpenRef.current = false;
        isTabChangeDialogOpenRef.current = false;
        tabChangeCountRef.current = 0;

        if (fullscreenDialogRef.current && document.body.contains(fullscreenDialogRef.current)) {
            document.body.removeChild(fullscreenDialogRef.current);
            fullscreenDialogRef.current = null;
        }
        if (tabChangeDialogRef.current && document.body.contains(tabChangeDialogRef.current)) {
            document.body.removeChild(tabChangeDialogRef.current);
            tabChangeDialogRef.current = null;
        }
    }, []);

    // Add cleanup function
    const cleanup = useCallback(() => {
        // Remove event listeners
        document.removeEventListener('copy', preventDefaultAction);
        document.removeEventListener('paste', preventDefaultAction);
        document.removeEventListener('cut', preventDefaultAction);
        document.removeEventListener('keydown', preventKeyboardShortcuts);
        document.removeEventListener('contextmenu', preventContextMenu);
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('beforeunload', blockRefresh);
        window.removeEventListener('popstate', blockBackButton);

        // Remove any existing dialogs and overlays
        resetWarningCount();
    }, [handleFullscreenChange, handleVisibilityChange, blockBackButton, blockRefresh, resetWarningCount]);

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }
            @keyframes fadeOut {
                from { opacity: 1; }
                to { opacity: 0; }
            }
        `;
        document.head.appendChild(style);

        const init = async () => {
            if (!isInitializedRef.current) {
                await requestFullScreen();
                isInitializedRef.current = true;
            }

            // Add event listeners
            document.addEventListener('copy', preventDefaultAction);
            document.addEventListener('paste', preventDefaultAction);
            document.addEventListener('cut', preventDefaultAction);
            document.addEventListener('keydown', preventKeyboardShortcuts);
            document.addEventListener('contextmenu', preventContextMenu);
            document.addEventListener('fullscreenchange', handleFullscreenChange);
            document.addEventListener('visibilitychange', handleVisibilityChange);
        };

        init();

        // Cleanup
        return () => {
            document.removeEventListener('copy', preventDefaultAction);
            document.removeEventListener('paste', preventDefaultAction);
            document.removeEventListener('cut', preventDefaultAction);
            document.removeEventListener('keydown', preventKeyboardShortcuts);
            document.removeEventListener('contextmenu', preventContextMenu);
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [handleFullscreenChange, handleVisibilityChange]);

    // Block browser refresh effect
    useEffect(() => {
        window.addEventListener('beforeunload', blockRefresh);

        return () => {
            window.removeEventListener('beforeunload', blockRefresh);
        };
    }, [blockRefresh]);

    // Block browser back button effect
    useEffect(() => {
        window.history.pushState(null, '', window.location.pathname);
        window.addEventListener('popstate', blockBackButton);

        return () => {
            window.removeEventListener('popstate', blockBackButton);
        };
    }, [blockBackButton]);

    return {
        resetWarningCount,
        cleanup
    };
};

export default useExamRestrictions; 